import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { useCatalog } from '../hooks/useCatalog'
import { useAuth } from '../hooks/useAuth'
import ProductCard from '../components/ProductCard'

function ArrowLeft({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function ArrowRight({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
}

export default function CatalogPage() {
  const { isDealer } = useAuth()
  const { categories, products, loading, error } = useCatalog(isDealer)
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeCategory, setActiveCategoryState] = useState('all')
  const carouselRef = useRef(null)

  // Read category from URL on mount
  useEffect(() => {
    const catFromUrl = searchParams.get('category')
    if (catFromUrl) {
      setActiveCategoryState(catFromUrl)
    }
  }, [searchParams])

  const setActiveCategory = useCallback((slug) => {
    setActiveCategoryState(slug)
    if (slug === 'all') {
      setSearchParams({})
    } else {
      setSearchParams({ category: slug })
    }
  }, [setSearchParams])

  const filteredProducts =
    activeCategory === 'all'
      ? products
      : products.filter((p) => {
          const cat = categories.find((c) => c.id === p.category_id)
          return cat?.slug === activeCategory
        })

  const scroll = useCallback((direction) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.querySelector('[data-card]')?.offsetWidth || 300
      const gap = 24
      carouselRef.current.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' })
    }
  }, [])

  return (
    <div className="min-h-screen bg-ss-white pt-16">
      <Helmet>
        <title>Каталог — SwedenSound</title>
        <meta name="description" content="Профессиональная акустика SwedenSound: H-Series, J-Series, R-Series, сабвуферы и микрофонные системы. Цены и технические характеристики." />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden text-white py-20">
        <div className="absolute inset-0" style={{ backgroundImage: `url('/backgrounds/bg-karaoke-club.jpg?v=2')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: '#1a1a1a' }} />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative mx-auto max-w-ss px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-light tracking-tight">Каталог</h1>
          <p className="mt-3 text-white/60 max-w-2xl mx-auto text-lg">
            Профессиональная акустика и микрофонные системы SwedenSound
          </p>
          {isDealer && (
            <div className="mt-4 inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-sm text-white">
              Видны дилерские цены
            </div>
          )}
        </div>
      </section>

      {/* Filter */}
      <div className="mx-auto max-w-ss px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={[
              'px-4 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-300',
              activeCategory === 'all' ? 'bg-ss-dark text-white' : 'bg-white text-ss-text border border-ss-border hover:border-ss-dark hover:text-ss-dark',
            ].join(' ')}
          >
            Все
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.slug)}
              className={[
                'px-4 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-300',
                activeCategory === cat.slug ? 'bg-ss-dark text-white' : 'bg-white text-ss-text border border-ss-border hover:border-ss-dark hover:text-ss-dark',
              ].join(' ')}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Carousel */}
      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="flex gap-6 overflow-hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex-shrink-0 w-[280px] h-96 rounded-xl bg-gray-100 animate-pulse border border-gray-200" />
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 p-8 text-center border border-red-100">
              <p className="text-red-700 font-medium">Ошибка загрузки каталога</p>
              <p className="text-red-500 text-sm mt-1">{error}</p>
              <button onClick={() => window.location.reload()} className="mt-4 text-sm font-medium text-red-700 underline">
                Обновить страницу
              </button>
            </div>
          )}

          {!loading && !error && filteredProducts.length > 0 && (
            <div className="relative">
              {/* Left arrow */}
              <button
                onClick={() => scroll(-1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-900 hover:text-white transition-all hover:scale-110 active:scale-95 -ml-5 hidden md:flex"
                aria-label="Влево"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Right arrow */}
              <button
                onClick={() => scroll(1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-900 hover:text-white transition-all hover:scale-110 active:scale-95 -mr-5 hidden md:flex"
                aria-label="Вправо"
              >
                <ArrowRight className="w-5 h-5" />
              </button>

              {/* Scrollable container */}
              <div
                ref={carouselRef}
                className="flex gap-6 overflow-x-auto pb-4 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {filteredProducts.map((product, index) => (
                  <div key={product.id} data-card className="flex-shrink-0 w-[280px] sm:w-[300px]">
                    <ProductCard product={product} index={index} isDealer={isDealer} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!loading && !error && filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">Нет товаров в выбранной категории</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
